/**
 * Alert Settings Page - Full User Control Panel
 * Comprehensive settings for AI Job Alerts
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useLanguage, COUNTRIES } from '@/lib/context';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Bell, BellOff, MapPin, Briefcase, Globe, Loader2, 
  Check, AlertCircle, Pause, Play, Settings, Mail, Clock,
  ChevronLeft, Zap, Calendar, Shield, Info, CheckCircle2,
  Activity, TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';
import { LocationAutocomplete } from '@/components/LocationAutocomplete';
import { ALL_COUNTRIES, getCountryByCode } from '@/lib/countries-data';

const API = process.env.REACT_APP_BACKEND_URL;

// Job types - using i18n keys
const JOB_TYPES = [
  { value: "job", labelKey: "alerts.jobTypeFulltime", icon: Briefcase },
  { value: "subcontracting", labelKey: "alerts.jobTypeSubcontracting", icon: TrendingUp },
  { value: "service_request", labelKey: "alerts.jobTypeServiceRequest", icon: Settings }
];

// Alert frequency options - using i18n keys
const FREQUENCY_OPTIONS = [
  { value: "instant", labelKey: "alerts.frequencyInstant", descriptionKey: "alerts.frequencyInstantDesc", icon: Zap },
  { value: "daily", labelKey: "alerts.frequencyDaily", descriptionKey: "alerts.frequencyDailyDesc", icon: Calendar },
  { value: "weekly", labelKey: "alerts.frequencyWeekly", descriptionKey: "alerts.frequencyWeeklyDesc", icon: Calendar }
];

export function AlertSettingsPage() {
  const { t } = useLanguage();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [systemStatus, setSystemStatus] = useState(null);
  const [preferences, setPreferences] = useState(null);
  const [hasPreferences, setHasPreferences] = useState(false);
  
  // Form state
  const [alertsEnabled, setAlertsEnabled] = useState(false);
  const [countries, setCountries] = useState([]);
  const [jobTypes, setJobTypes] = useState(['job']);
  const [frequency, setFrequency] = useState('instant');
  const [matchThreshold, setMatchThreshold] = useState(70);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (token) {
      fetchPreferences();
      fetchSystemStatus();
    }
  }, [token]);

  const fetchPreferences = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/api/job-alerts/preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data) {
          setPreferences(data);
          setHasPreferences(true);
          setAlertsEnabled(data.email_enabled !== false);
          setCountries(data.countries || []);
          setJobTypes(data.listing_types || ['job']);
          setFrequency(data.frequency);
          setMatchThreshold(data.match_threshold || 70);
          setPaused(data.paused || false);
        }
      }
    } catch (error) {
      console.error('Failed to fetch preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSystemStatus = async () => {
    try {
      // For non-admin users, we just show basic status
      setSystemStatus({ engine_enabled: true });
    } catch (error) {
      console.error('Failed to fetch system status:', error);
    }
  };

  const handleSave = async () => {
    if (alertsEnabled && countries.length === 0) {
      toast.error(t('alerts.selectCountryError'));
      return;
    }

    setSaving(true);
    try {
      const payload = {
        countries,
        listing_types: jobTypes,
        frequency,
        match_threshold: matchThreshold,
        email_enabled: alertsEnabled
      };

      const method = hasPreferences ? 'PUT' : 'POST';
      const response = await fetch(`${API}/api/job-alerts/preferences`, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        setPreferences(data);
        setHasPreferences(true);
        toast.success(t('alerts.preferencesSaved'));
      } else {
        const error = await response.json();
        toast.error(error.detail);
      }
    } catch (error) {
      toast.error(t('alerts.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePause = async () => {
    try {
      const endpoint = paused ? 'resume' : 'pause';
      const response = await fetch(`${API}/api/job-alerts/preferences/${endpoint}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        setPaused(!paused);
        toast.success(paused ? t('alerts.resumed') : t('alerts.paused'));
      }
    } catch (error) {
      toast.error(t('alerts.updateStatusFailed'));
    }
  };

  const toggleCountry = (country) => {
    setCountries(prev =>
      prev.includes(country)
        ? prev.filter(c => c !== country)
        : [...prev, country]
    );
  };

  const toggleJobType = (type) => {
    setJobTypes(prev =>
      prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  // Format timestamp
  const formatDate = (dateStr) => {
    if (!dateStr) return t('alerts.never');
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    
    if (hours < 1) return t('alerts.justNow');
    if (hours < 24) return t('alerts.hoursAgo', { count: hours });
    if (days < 7) return t('alerts.daysAgo', { count: days });
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <Bell className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <h1 className="text-xl font-semibold mb-2">{t('auth.loginRequired')}</h1>
            <p className="text-gray-600 mb-4">{t('alerts.loginToManage')}</p>
            <Button onClick={() => navigate('/login')}>{t('auth.login')}</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ChevronLeft className="h-4 w-4 mr-1" />
            {t('common.back')}
          </Button>
          <h1 className="font-semibold">{t('alerts.title')}</h1>
          <div className="w-16" />
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        {/* System Status */}
        <Alert className="bg-green-50 border-green-200">
          <Activity className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-700">
            <span className="font-medium">{t('alerts.matchingEngineActive')}</span> — {t('alerts.matchingEngineActiveDesc')}
          </AlertDescription>
        </Alert>

        {/* Main Toggle Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="h-5 w-5" />
                  {t('alerts.jobAlerts')}
                </CardTitle>
                <CardDescription>
                  {t('alerts.jobAlertsDesc')}
                </CardDescription>
              </div>
              <Switch
                checked={alertsEnabled}
                onCheckedChange={setAlertsEnabled}
                className="scale-125"
              />
            </div>
          </CardHeader>
          
          {hasPreferences && (
            <CardContent className="pt-0">
              <div className="flex items-center gap-4 text-sm">
                {/* Status Badge */}
                <Badge 
                  variant={paused ? "secondary" : alertsEnabled ? "default" : "outline"}
                  className={
                    paused ? "bg-yellow-100 text-yellow-700" : 
                    alertsEnabled ? "bg-green-100 text-green-700" : ""
                  }
                >
                  {paused ? (
                    <><Pause className="h-3 w-3 mr-1" /> {t('alerts.statusPaused')}</>
                  ) : alertsEnabled ? (
                    <><CheckCircle2 className="h-3 w-3 mr-1" /> {t('alerts.statusActive')}</>
                  ) : (
                    <><BellOff className="h-3 w-3 mr-1" /> {t('alerts.statusDisabled')}</>
                  )}
                </Badge>
                
                {/* Stats */}
                {preferences?.alerts_sent > 0 && (
                  <span className="text-gray-500">
                    {preferences.alerts_sent} {t('alerts.alertsSent')}
                  </span>
                )}
                
                {preferences?.last_alert_at && (
                  <span className="text-gray-500 flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {t('alerts.lastAlert')}: {formatDate(preferences.last_alert_at)}
                  </span>
                )}
              </div>
              
              {/* Pause/Resume */}
              {alertsEnabled && hasPreferences && (
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  onClick={handleTogglePause}
                >
                  {paused ? (
                    <><Play className="h-4 w-4 mr-1" /> {t('alerts.resumeAlerts')}</>
                  ) : (
                    <><Pause className="h-4 w-4 mr-1" /> {t('alerts.pauseTemporarily')}</>
                  )}
                </Button>
              )}
            </CardContent>
          )}
        </Card>

        {/* Country Selection */}
        <Card className={!alertsEnabled ? 'opacity-50 pointer-events-none' : ''}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <MapPin className="h-5 w-5" />
              {t('alerts.countries')}
            </CardTitle>
            <CardDescription>
              {t('alerts.countriesDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {COUNTRIES.map((country) => (
                <div
                  key={country.code}
                  onClick={() => toggleCountry(country.code)}
                  className={`
                    flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all
                    ${countries.includes(country.code)
                      ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}
                  `}
                >
                  <span className="text-2xl">{country.flag}</span>
                  <div className="flex-1">
                    <span className="font-medium text-sm">{t(country.nameKey)}</span>
                  </div>
                  <Checkbox 
                    checked={countries.includes(country.code)}
                    className="pointer-events-none"
                  />
                </div>
              ))}
            </div>
            
            {alertsEnabled && countries.length === 0 && (
              <Alert className="mt-4 bg-amber-50 border-amber-200">
                <AlertCircle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-700">
                  {t('alerts.selectCountryWarning')}
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Job Types */}
        <Card className={!alertsEnabled ? 'opacity-50 pointer-events-none' : ''}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Briefcase className="h-5 w-5" />
              {t('alerts.jobTypes')}
            </CardTitle>
            <CardDescription>
              {t('alerts.jobTypesDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {JOB_TYPES.map((type) => (
                <div
                  key={type.value}
                  onClick={() => toggleJobType(type.value)}
                  className={`
                    flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all
                    ${jobTypes.includes(type.value)
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'}
                  `}
                >
                  <type.icon className={`h-5 w-5 ${jobTypes.includes(type.value) ? 'text-blue-600' : 'text-gray-400'}`} />
                  <span className="font-medium">{t(type.labelKey)}</span>
                  <div className="flex-1" />
                  <Checkbox 
                    checked={jobTypes.includes(type.value)}
                    className="pointer-events-none"
                  />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Alert Frequency */}
        <Card className={!alertsEnabled ? 'opacity-50 pointer-events-none' : ''}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Clock className="h-5 w-5" />
              {t('alerts.alertFrequency')}
            </CardTitle>
            <CardDescription>
              {t('alerts.alertFrequencyDesc')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <RadioGroup value={frequency} onValueChange={setFrequency}>
              <div className="space-y-3">
                {FREQUENCY_OPTIONS.map((option) => (
                  <label
                    key={option.value}
                    className={`
                      flex items-start gap-4 p-4 rounded-lg border cursor-pointer transition-all
                      ${frequency === option.value
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'}
                    `}
                  >
                    <RadioGroupItem value={option.value} className="mt-0.5" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <option.icon className={`h-4 w-4 ${frequency === option.value ? 'text-blue-600' : 'text-gray-400'}`} />
                        <span className="font-medium">{t(option.labelKey)}</span>
                        {option.value === 'instant' && (
                          <Badge variant="secondary" className="text-xs">{t('alerts.recommended')}</Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-1">{t(option.descriptionKey)}</p>
                    </div>
                  </label>
                ))}
              </div>
            </RadioGroup>
          </CardContent>
        </Card>

        {/* Match Sensitivity */}
        <Card className={!alertsEnabled ? 'opacity-50 pointer-events-none' : ''}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Settings className="h-5 w-5" />
              {t('alerts.matchSensitivity')}
            </CardTitle>
            <CardDescription>
              {t('alerts.matchThresholdDesc')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-2xl font-bold">{matchThreshold}%</span>
              <Badge variant="outline">
                {matchThreshold >= 85 ? t('alerts.veryStrict') : matchThreshold >= 70 ? t('alerts.balanced') : t('alerts.moreAlerts')}
              </Badge>
            </div>
            
            <Slider
              value={[matchThreshold]}
              onValueChange={(v) => setMatchThreshold(v[0])}
              min={50}
              max={95}
              step={5}
              className="py-4"
            />
            
            <div className="flex justify-between text-xs text-gray-500">
              <span>{t('alerts.moreAlertsPercent')}</span>
              <span>{t('alerts.fewerBetterMatches')}</span>
            </div>
            
            <Alert className="bg-gray-50">
              <Info className="h-4 w-4" />
              <AlertDescription className="text-sm">
                {matchThreshold >= 85 
                  ? t('alerts.matchThresholdExcellent')
                  : matchThreshold >= 70 
                  ? t('alerts.matchThresholdGood')
                  : t('alerts.matchThresholdMore')}
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>

        {/* Privacy & Unsubscribe Info */}
        <Card className="bg-gray-50">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <Shield className="h-5 w-5 text-gray-400 mt-0.5" />
              <div className="text-sm text-gray-600">
                <p className="font-medium text-gray-700 mb-1">{t('alerts.yourPrivacy')}</p>
                <p>
                  {t('alerts.privacyDesc')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end gap-3 pt-4">
          <Button variant="outline" onClick={() => navigate(-1)}>
            {t('common.cancel')}
          </Button>
          <Button
            size="lg"
            onClick={handleSave}
            disabled={saving || (alertsEnabled && countries.length === 0)}
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Check className="h-4 w-4 mr-2" />
            )}
            {t('alerts.savePreferences')}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default AlertSettingsPage;
